import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, Star, Clock } from "lucide-react";
import { SourceChip, SourceIcon } from "@/components/app/SourceIcon";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, Empty, PageHeader, StatusBadge } from "@/components/app/StatusBadge";
import { api, fmtTime } from "@/lib/api";
import { useApp } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";

export default function Meetings() {
  const { L } = useApp();
  const { workspace } = useAuth();
  const [params] = useSearchParams();
  const [items, setItems] = useState(null);
  const [folders, setFolders] = useState([]);
  const [f, setF] = useState({ q: "", folder_id: params.get("folder") || "all", tag: "", favourite: false, date_from: "", date_to: "" });

  useEffect(() => {
    if (workspace) api.get("/folders", { params: { workspace_id: workspace.id } }).then((r) => setFolders(r.data));
  }, [workspace]);
  useEffect(() => {
    if (!workspace) return;
    const t = setTimeout(() => api.get("/meetings", { params: { workspace_id: workspace.id, ...f, folder_id: f.folder_id === "all" ? "" : f.folder_id } }).then((r) => setItems(r.data)), 250);
    return () => clearTimeout(t);
  }, [workspace, f]);

  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const toggleFav = async (m) => {
    await api.patch(`/meetings/${m.id}`, { favourite: !m.favourite });
    setItems((l) => l.map((x) => (x.id === m.id ? { ...x, favourite: !m.favourite } : x)));
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-8" data-testid="meetings-page">
      <PageHeader title={L("Mesyuarat Saya", "My Meetings")} desc={L("Cari, tapis dan urus semua mesyuarat dalam workspace ini.", "Search, filter and manage all meetings in this workspace.")} />
      <Card className="mt-6 grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-6">
        <div className="relative lg:col-span-2">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={f.q} onChange={(e) => set("q", e.target.value)} placeholder={L("Cari tajuk…", "Search title…")} className="h-10 rounded-xl pl-9" data-testid="meeting-search-input" aria-label="Cari" />
        </div>
        <Select value={f.folder_id} onValueChange={(v) => set("folder_id", v)}>
          <SelectTrigger className="h-10 rounded-xl" data-testid="filter-folder"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">{L("Semua folder", "All folders")}</SelectItem>{folders.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent>
        </Select>
        <Input type="date" value={f.date_from} onChange={(e) => set("date_from", e.target.value)} className="h-10 rounded-xl" aria-label="Dari tarikh" data-testid="filter-date-from" />
        <Input type="date" value={f.date_to} onChange={(e) => set("date_to", e.target.value)} className="h-10 rounded-xl" aria-label="Hingga tarikh" data-testid="filter-date-to" />
        <button onClick={() => set("favourite", !f.favourite)} aria-pressed={f.favourite} data-testid="filter-favourite" className={`flex h-10 items-center justify-center gap-2 rounded-xl border text-sm font-medium ${f.favourite ? "border-amber-300 bg-amber-50 text-amber-800" : "border-slate-200"}`}>
          <Star size={14} className={f.favourite ? "fill-amber-400 text-amber-400" : ""} /> {L("Kegemaran", "Favourites")}
        </button>
      </Card>
      {f.tag && <button onClick={() => set("tag", "")} className="mt-3 rounded-full bg-violet-100 px-3 py-1 text-xs font-semibold text-violet-800" data-testid="active-tag-filter">#{f.tag} ×</button>}
      <div className="mt-5 space-y-3">
        {items && items.length === 0 && <Empty testId="meetings-empty">{L("Tiada mesyuarat ditemui.", "No meetings found.")}</Empty>}
        {items?.map((m) => (
          <Card key={m.id} className="flex flex-wrap items-center gap-4 p-4 transition-shadow hover:shadow-md" data-testid={`meeting-row-${m.id}`}>
            <SourceIcon source={m.source} testId={`meeting-source-${m.id}`} />
            <Link to={`/app/meetings/${m.id}`} className="min-w-0 flex-1">
              <p className="truncate font-semibold hover:text-violet-700">{m.title}</p>
              <p className="mt-0.5 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                <SourceChip source={m.source} /><span>{m.meeting_date}</span><span className="flex items-center gap-1"><Clock size={11} /> {fmtTime(m.duration)}</span>
              </p>
            </Link>
            <div className="flex flex-wrap gap-1">{m.tags?.map((t) => <button key={t} onClick={() => set("tag", t)} className="rounded-full bg-slate-100 px-2 py-0.5 text-xs">#{t}</button>)}</div>
            <StatusBadge status={m.status} />
            <button onClick={() => toggleFav(m)} aria-label="Kegemaran" data-testid={`fav-toggle-${m.id}`}><Star size={16} className={m.favourite ? "fill-amber-400 text-amber-400" : "text-slate-300"} /></button>
          </Card>
        ))}
      </div>
    </div>
  );
}
